import React, { useState, useEffect, useRef } from 'react';

// --- TERMS CONTENT (extracted for reuse) ---
const TermsContent = () => (
  <>
    <section>
      <h3>1. Geltungsbereich &amp; MVP-Status</h3>
      <p>
        Diese Plattform wird als <strong>Minimum Viable Product (MVP)</strong>
        einer ausgewählten Nutzergruppe zur Verfügung gestellt.
        Funktionen können jederzeit geändert, eingeschränkt oder eingestellt werden.
      </p>
    </section>

    <section>
      <h3>2. Leistungen</h3>
      <p>
        Die Plattform bietet Funktionen zur Kalenderverwaltung,
        Essens- und Wochenplanung, Rezeptverwaltung, Einkaufslisten,
        Aufgabenmanagement sowie KI-gestützte Vorschläge.
        Ein bestimmtes Ergebnis oder eine fehlerfreie Funktionalität
        wird nicht geschuldet.
      </p>
    </section>

    <section>
      <h3>3. KI-Funktionen</h3>
      <p>
        KI-generierte Inhalte erfolgen automatisiert und sind unverbindlich.
        Sie können unvollständig oder fehlerhaft sein.
        Die Nutzung der Ergebnisse erfolgt auf eigene Verantwortung.
      </p>
    </section>

    <section>
      <h3>4. Gesundheits- &amp; Ernährungshinweis</h3>
      <p>
        KI-Vorschläge stellen <strong>keine medizinische oder ernährungs-
          wissenschaftliche Beratung</strong> dar.
        Zutaten, Allergene und Verträglichkeiten sind stets eigenständig zu prüfen.
      </p>
    </section>

    <section>
      <h3>5. Verarbeitung sensibler Daten (Art. 9 DSGVO)</h3>
      <p>
        Du kannst freiwillig sensible Daten wie Allergien,
        Ernährungsweisen oder Einschränkungen angeben.
        Diese Daten werden <strong>ausschließlich mit deiner ausdrücklichen
          Einwilligung</strong> verarbeitet, um personalisierte KI-Funktionen
        zu ermöglichen.
      </p>
      <p>
        Die Einwilligung kann jederzeit widerrufen werden.
        In diesem Fall werden die betroffenen KI-Funktionen deaktiviert,
        dein Benutzerkonto bleibt jedoch bestehen.
      </p>
    </section>

    <section>
      <h3>6. Geteilte Arbeitsbereiche</h3>
      <p>
        Inhalte in gemeinsam genutzten Bereichen sind für alle eingeladenen
        Mitglieder sichtbar. Du bist verantwortlich für die Inhalte,
        die du mit anderen teilst.
      </p>
    </section>

    <section>
      <h3>7. Nutzerinhalte &amp; Rezepte</h3>
      <p>
        Private Rezepte bleiben dein Eigentum.
        Öffentlich geteilte Rezepte dürfen innerhalb der Plattform
        angezeigt werden.
      </p>
    </section>

    <section>
      <h3>8. Haftung</h3>
      <p>
        Die Plattform wird „wie gesehen" bereitgestellt.
        Eine Haftung besteht nur bei Vorsatz und grober Fahrlässigkeit.
        Keine Haftung wird für KI-Ausgaben, Datenverluste oder
        Drittanbieter-Integrationen übernommen.
      </p>
    </section>

    <section>
      <h3>9. Anwendbares Recht</h3>
      <p>
        Es gilt das Recht der <strong>Bundesrepublik Deutschland</strong>.
      </p>
    </section>

    <hr />
    <p style={{ textAlign: 'center', color: '#666' }}>
      Ende der MVP-Nutzungsbedingungen
    </p>
  </>
);

const TermsAndConditionsOverlay = ({ isVisible, onAccept }) => {
  const scrollRef = useRef(null);

  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [hasCheckedTerms, setHasCheckedTerms] = useState(false);
  const [hasCheckedSensitiveData, setHasCheckedSensitiveData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Lock body scroll when overlay is visible
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    }
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    };
  }, [isVisible]);

  // Scroll-to-bottom detection
  useEffect(() => {
    if (!isVisible) return;
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 10;
      setIsScrolledToBottom(atBottom);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    // Check immediately in case content is short enough to not need scrolling
    setTimeout(onScroll, 100);
    return () => el.removeEventListener('scroll', onScroll);
  }, [isVisible]);

  const handleAccept = async () => {
    if (isScrolledToBottom && hasCheckedTerms && hasCheckedSensitiveData && !isSubmitting) {
      try {
        setIsSubmitting(true);
        await onAccept();
      } catch (err) {
        console.error('Fehler beim Akzeptieren:', err);
        setIsSubmitting(false);
      }
    }
  };

  const isAcceptEnabled =
    isScrolledToBottom && hasCheckedTerms && hasCheckedSensitiveData;

  if (!isVisible) return null;

  return (
    <>
      <div className="terms-overlay">
        {/* Scrollable area: everything scrolls together */}
        <div className="terms-scroll-area" ref={scrollRef}>
          <div className="terms-content-wrapper">
            <h2 className="terms-title">
              Nutzungsbedingungen &amp; Datenschutz (MVP)
            </h2>

            <p className="terms-instruction">
              Bitte scrolle durch das gesamte Dokument und bestätige beide Einwilligungen.
            </p>

            <div className="terms-text">
              <TermsContent />
            </div>
          </div>
        </div>

        {/* Sticky footer with checkboxes and button */}
        <div className="terms-footer">
          <label className="terms-checkbox">
            <input
              type="checkbox"
              checked={hasCheckedTerms}
              onChange={(e) => setHasCheckedTerms(e.target.checked)}
            />
            <span>
              Ich stimme den <strong>Nutzungsbedingungen</strong> zu und habe
              die <strong>Datenschutzerklärung</strong> gelesen.
            </span>
          </label>

          <label className="terms-checkbox">
            <input
              type="checkbox"
              checked={hasCheckedSensitiveData}
              onChange={(e) => setHasCheckedSensitiveData(e.target.checked)}
            />
            <span>
              Ich willige <strong>ausdrücklich</strong> in die Verarbeitung
              meiner sensiblen Ernährungs- und Gesundheitsdaten zur
              KI-gestützten Personalisierung ein.
              Mir ist bekannt, dass ich diese Einwilligung jederzeit
              widerrufen kann.
            </span>
          </label>

          <button
            className={`terms-accept-btn ${(!isAcceptEnabled || isSubmitting) ? 'disabled' : ''}`}
            onClick={handleAccept}
            disabled={!isAcceptEnabled || isSubmitting}
          >
            {isSubmitting ? 'Wird verarbeitet …' : 'Akzeptieren & fortfahren'}
          </button>
        </div>
      </div>

      <style>{`
        .terms-overlay {
          position: fixed;
          top: 0; left: 0;
          width: 100%; height: 100%;
          background-color: rgba(0, 0, 0, 0.85);
          z-index: 10000;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        /* ---- SCROLLABLE AREA ---- */
        .terms-scroll-area {
          flex: 1;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          display: flex;
          justify-content: center;
        }

        .terms-content-wrapper {
          width: 100%;
          max-width: 750px;
          padding: 24px 20px 16px;
        }

        .terms-title {
          color: #fff;
          font-size: 1.4rem;
          margin: 0 0 8px 0;
        }

        .terms-instruction {
          color: #f87171;
          font-weight: bold;
          font-size: 0.85rem;
          margin-bottom: 16px;
        }

        .terms-text {
          background: #fff;
          border-radius: 8px;
          padding: 20px;
          line-height: 1.6;
          font-size: 0.9rem;
          color: #333;
        }

        .terms-text h3 {
          font-size: 1.05rem;
          margin-top: 16px;
        }

        .terms-text section:first-child h3 {
          margin-top: 0;
        }

        /* ---- STICKY FOOTER ---- */
        .terms-footer {
          flex-shrink: 0;
          background: #fff;
          border-top: 2px solid #e5e7eb;
          padding: 12px 20px;
          padding-bottom: max(12px, env(safe-area-inset-bottom));
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .terms-footer > * {
          width: 100%;
          max-width: 750px;
        }

        .terms-checkbox {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          cursor: pointer;
          font-size: 0.82rem;
          color: #333;
          line-height: 1.4;
        }

        .terms-checkbox input[type="checkbox"] {
          margin-top: 3px;
          flex-shrink: 0;
          width: 18px;
          height: 18px;
          accent-color: #3b82f6;
        }

        .terms-accept-btn {
          padding: 12px 20px;
          background-color: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          width: 100%;
          max-width: 750px;
          cursor: pointer;
          font-weight: bold;
          font-size: 0.95rem;
          transition: background-color 0.2s;
        }

        .terms-accept-btn:hover:not(.disabled) {
          background-color: #2563eb;
        }

        .terms-accept-btn.disabled {
          background-color: #9ca3af;
          cursor: not-allowed;
        }

        /* ---- DESKTOP: Centered card look ---- */
        @media (min-width: 768px) {
          .terms-scroll-area {
            align-items: center;
            padding: 40px 20px;
          }

          .terms-content-wrapper {
            background: transparent;
            padding: 0;
          }

          .terms-title {
            font-size: 1.6rem;
          }

          .terms-footer {
            padding: 16px 24px;
          }
        }
      `}</style>
    </>
  );
};

export default TermsAndConditionsOverlay;
